import { type QueryResult, type OperationVariables } from '@apollo/react-common';
import {
	type QueryHookOptions,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { type GraphQLTaggedNode, fetchQuery, useRelayEnvironment } from 'react-relay';
import { useCallback, useState, useEffect } from 'react';
import { type DocumentNode } from 'graphql/language/ast';

import { useMemoObj } from './useMemoObj';

// For now, only a limited number of parameters are supported in a Relay variant.
// This type serves as an indicator of what's supported.
// To find all the unsupported usages, `UseQueryInteropCompatibleHookValues` may be replaced with it - then Typecheck will highlight everything that's broken
type SupportedUseQueryInteropCompatibleHookValues<TData, TVariables> = Pick<
	QueryResult<TData, TVariables>,
	'data' | 'error' | 'loading' | 'refetch'
>;
export type UseQueryInteropCompatibleHookValues<TData, TVariables> = QueryResult<TData, TVariables>;

export type UseQueryInteropParams<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> =
	| [DocumentNode | GraphQLTaggedNode]
	| [DocumentNode | GraphQLTaggedNode, QueryHookOptions<TData, TVariables>];

export function useQueryInterop<TData, TVariables extends Record<string, any> = OperationVariables>(
	...[query, params]: UseQueryInteropParams<TData, TVariables>
): UseQueryInteropCompatibleHookValues<TData, TVariables> {
	return process.env.BUILD_VARIANT === 'relay'
		? (__useQueryInterop_Relay<TData, TVariables>(...[query as GraphQLTaggedNode, params]) as any)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useQuery<TData, TVariables>(...[query as DocumentNode, params]);
}

function __useQueryInterop_Relay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	query: GraphQLTaggedNode,
	options?: Parameters<typeof useQuery<TData, TVariables>>[1],
): SupportedUseQueryInteropCompatibleHookValues<TData, TVariables> {
	// Extract variables and memoize them to prevent unnecessary re-renders
	const variables = useMemoObj<TVariables>(options?.variables);

	const skip = options?.skip || false;
	const environment = useRelayEnvironment();

	// State to manage the query result
	const [data, setData] = useState<TData | undefined>(undefined);
	const [loading, setLoading] = useState(!skip);
	const [error, setError] = useState<any>(undefined);

	// Use fetchQuery to get data
	useEffect(() => {
		if (skip) {
			setLoading(false);
			setData(undefined);
			setError(undefined);
			return;
		}

		setLoading(true);
		setError(undefined);

		const subscription = fetchQuery<any>(environment, query, variables).subscribe({
			next: (result) => {
				setData(result);
				setLoading(false);
			},
			error: (err: any) => {
				setError(err);
				setLoading(false);
			},
		});

		return () => subscription.unsubscribe();
	}, [environment, query, variables, skip]);

	// Create a refetch function for Apollo compatibility
	const refetch = useCallback(
		(newVariables?: TVariables) => {
			const varsToUse = newVariables || variables;
			setLoading(true);
			setError(undefined);

			return fetchQuery<any>(environment, query, varsToUse)
				.toPromise()
				.then((result) => {
					setData(result);
					setLoading(false);
					return {
						data: result as TData,
						loading: false,
						networkStatus: 7, // NetworkStatus.ready
						stale: false,
					};
				})
				.catch((err) => {
					setError(err);
					setLoading(false);
					throw err;
				});
		},
		[environment, query, variables],
	);

	return {
		data,
		loading,
		error,
		refetch,
	};
}
